"use client";

import {
  Avatar,
  Center,
  Drawer,
  Flex,
  ScrollAreaAutosize,
  Stack,
  Tabs,
  Text,
} from "@mantine/core";
import { yupResolver } from "@mantine/form";
import { useQuery } from "@tanstack/react-query";
import { parseAsBoolean, useQueryState } from "nuqs";
import { useEffect } from "react";
import { object, string } from "yup";

import { builder } from "@/builders";
import { ProfileDetail } from "@/components/staff/profile/profile-detail";
import { cast } from "@/libraries/cast";
import { pass } from "@/libraries/pass";

import { FormProvider, useForm } from "./edit-profile-form-context";
import { OverviewPanel } from "./overview-panel";
import { SettingsPanel } from "./settings-panel";

const schema = object({
  first_name: string().required("Firstname is required"),
  last_name: string().required("Lastname is required"),
  phone_number: string().required("Phone number is required"),
  department_id: string().required("Module is required"),
  career_path_id: string().required("Career is required"),
});

interface ProfileDrawerProps {}

export function ProfileDrawer({}: ProfileDrawerProps) {
  const [opened, setOpened] = useQueryState(
    "drawer",
    parseAsBoolean.withDefault(false)
  );

  const [panel, setPanel] = useQueryState("panel", {
    defaultValue: "overview",
  });

  const { data, isLoading } = useQuery({
    queryKey: builder.account.profile.get.$get(),
    queryFn: () => builder.$use.account.profile.get(),
    select: ({ data }) => data,
  });

  const { data: badge } = useQuery({
    queryKey: builder.data.badge.$get(),
    queryFn: builder.$use.data.badge,
    select: ({ data }) => data,
  });

  const form = useForm({
    initialValues: {
      first_name: "",
      last_name: "",
      phone_number: "",
      department_id: "",
      career_path_id: "",
      rank_id: "",
      edit_profile: false,
    },
    validate: yupResolver(schema),
    validateInputOnBlur: true,
    transformValues: (values) => {
      const {
        first_name,
        last_name,
        phone_number,
        department_id,
        career_path_id,
      } = values;

      return {
        first_name: cast.string(first_name),
        last_name: cast.string(last_name),
        phone_number: cast.string(phone_number),
        department_id: cast.number(department_id),
        career_path_id: cast.number(career_path_id),
      };
    },
  });

  useEffect(() => {
    if (isLoading) return;

    const {
      first_name,
      last_name,
      phone_number,
      department_id,
      career_path_id,
      rank_id,
    } = { ...data };

    form.initialize({
      first_name: pass.string(first_name),
      last_name: pass.string(last_name),
      phone_number: pass.string(phone_number),
      department_id: pass.string(department_id),
      career_path_id: pass.string(career_path_id),
      rank_id: pass.string(rank_id),
      edit_profile: false,
    });
  }, [isLoading]);

  return (
    <Drawer
      lockScroll
      position="right"
      scrollAreaComponent={ScrollAreaAutosize}
      size={1045}
      opened={opened}
      onClose={() => setOpened(false)}
      keepMounted
      styles={{
        header: {
          paddingBlock: 24,
          paddingInline: 32,
        },
        body: {
          paddingInline: 0,
        },
      }}
      title={
        <Stack gap={0}>
          <h2 className="prose-xl/medium text-primary-text-body">
            Profile Overview
          </h2>
          <Text className="text-primary-text-subtle prose-sm/regular">
            Home - Profile
          </Text>
        </Stack>
      }
    >
      <Stack className="bg-primary-background-white">
        <Stack gap={32}>
          <Tabs flex={1} value={panel} onChange={setPanel}>
            <Stack
              px={32}
              pt={32}
              gap={32}
              style={{
                backgroundImage: "url('/images/profile-background.png')",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
              }}
            >
              <Flex justify="space-between">
                <Flex gap={18} align="center">
                  <Avatar size="108" radius="50%" src={data?.image} />

                  <Stack gap={4} align="flex-start">
                    <Flex justify="center" align="center" gap={4}>
                      <Text className="text-2xl font-medium text-primary-text-body">
                        {data?.first_name}
                      </Text>
                      <Center
                        p={8}
                        bg="accent.2"
                        fw={600}
                        lh={1}
                        className="rounded text-primary-button-normal"
                      >
                        Pro
                      </Center>
                    </Flex>

                    <Flex columnGap={16} wrap="wrap">
                      <ProfileDetail label="Role" value={data?.user_type} />
                      <ProfileDetail label="ID" value={data?.staff_id} />
                      <ProfileDetail label="Email" value={data?.email} />
                    </Flex>
                  </Stack>
                </Flex>

                <Stack gap={6} align="center">
                  <Avatar src={badge?.badge_url} size={60} />
                  <Text className="text-xl font-medium text-right text-primary-text-body">
                    {badge?.badge_name}
                  </Text>
                </Stack>
              </Flex>

              <Tabs.List>
                <Tabs.Tab value="overview">Overview</Tabs.Tab>
                <Tabs.Tab value="settings">Settings</Tabs.Tab>
              </Tabs.List>
            </Stack>

            <FormProvider form={form}>
              <Tabs.Panel value="overview">
                <OverviewPanel setPanel={setPanel} {...data} />
              </Tabs.Panel>
              <Tabs.Panel value="settings">
                <SettingsPanel {...data} />
              </Tabs.Panel>
            </FormProvider>
          </Tabs>
        </Stack>
      </Stack>
    </Drawer>
  );
}
